import React, { useState, useCallback } from 'react';
import { Search, Sparkles, FileText, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';

interface AIProductSearchProps {
  onSearch: (query: string, method: 'ai' | 'manual') => void;
  isSearching?: boolean;
}

interface ProductSuggestion {
  id: string;
  brand: string;
  model: string;
  category: string;
}

export function AIProductSearch({ onSearch, isSearching = false }: AIProductSearchProps) {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncedQuery = useDebounce(query, 300);

  // Search existing products for suggestions
  const { data: suggestions = [] } = useQuery({
    queryKey: ['product-suggestions', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) return [];

      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('product_library')
        .select('id, brand, model, category')
        .or(`brand.ilike.%${debouncedQuery}%,model.ilike.%${debouncedQuery}%`)
        .limit(5);

      if (error) throw error;
      return data as ProductSuggestion[];
    },
    enabled: debouncedQuery.length >= 2,
  });

  const handleSearch = useCallback(
    (method: 'ai' | 'manual') => {
      if (!query.trim() && method === 'ai') return;
      onSearch(query.trim(), method);
      setShowSuggestions(false);
    },
    [query, onSearch],
  );

  const handleSuggestionClick = (suggestion: ProductSuggestion) => {
    setQuery(`${suggestion.brand} ${suggestion.model}`);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Stop propagation to prevent floor plan keyboard shortcuts
    if (e.key === ' ') {
      e.stopPropagation();
    }

    if (e.key === 'Enter' && !isSearching) {
      handleSearch('ai');
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <p className="text-muted-foreground">
          Search for a product model to auto-populate specifications
        </p>
      </div>

      <div className="relative max-w-2xl mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Enter product model (e.g., SONOS Era 300)"
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyDown={handleKeyDown}
            onKeyPress={e => e.stopPropagation()}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="pl-10 pr-4 h-12 text-lg border focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-primary/40 focus-visible:border-primary/50"
            disabled={isSearching}
          />
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <Card className="absolute top-full left-0 right-0 mt-1 p-2 z-50 shadow-lg">
            <div className="text-xs text-muted-foreground px-2 py-1">
              Existing products in library:
            </div>
            {suggestions.map(suggestion => (
              <button
                key={suggestion.id}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left px-3 py-2 hover:bg-muted rounded-md transition-colors"
              >
                <div className="font-medium">
                  {suggestion.brand} {suggestion.model}
                </div>
                <div className="text-xs text-muted-foreground capitalize">
                  {suggestion.category.replace(/_/g, ' ')}
                </div>
              </button>
            ))}
          </Card>
        )}
      </div>

      <div className="flex gap-3 justify-center">
        <Button
          size="lg"
          onClick={() => handleSearch('ai')}
          disabled={!query.trim() || isSearching}
          className="gap-2"
        >
          {isSearching ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Search with AI
            </>
          )}
        </Button>

        <Button
          size="lg"
          variant="outline"
          onClick={() => handleSearch('manual')}
          disabled={isSearching}
          className="gap-2"
        >
          <FileText className="h-4 w-4" />
          Manual Entry
        </Button>
      </div>

      {query.trim() && (
        <div className="text-center">
          <p className="text-sm text-muted-foreground">AI search will find details automatically</p>
        </div>
      )}
    </div>
  );
}
